import { Box, Button, Checkbox, Group, Stack, Table, Text } from "@mantine/core"
import { ShoppingCartSimpleIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { currencyFormat } from "../../../utils/currency"
import { Dot, DotPill, type Tone } from "../../delivery/atoms"
import { ListState } from "../../ui"
import { ProductThumb } from "../product-list/ProductThumb"
import qoClasses from "../quick-order/quick-order.module.css"
import classes from "./saved-lists.module.css"
import type { CheckedLine, LineStatus } from "./use-reorder-lines"

const STATUS_TONE: Record<LineStatus, Tone> = {
  OK: "green",
  PRICE_CHANGED: "grey",
  LOW_STOCK: "amber",
  OUT: "red",
  NO_PRICE: "amber",
  NOT_FOUND: "red",
}

/** Canvas: under 10 pieces reads «Цөөн». */
const FEW_STOCK = 10

type Props = {
  title: string
  date: string
  lines: CheckedLine[]
  isLoading: boolean
  error: unknown
  isSelected: (line: CheckedLine) => boolean
  onToggle: (line: CheckedLine) => void
  onRetry: () => void
  onAdd: () => void
}

/** Right panel of OrdB_SavedLists: «<name> дахин захиалах», today's price and stock per line. */
export function ReorderPanel({ title, date, lines, isLoading, error, isSelected, onToggle, onRetry, onAdd }: Readonly<Props>) {
  const { t } = useTranslation("bulkOrder")
  const selected = lines.filter(isSelected)
  const total = selected.reduce((sum, l) => sum + l.currentPrice * l.addQty, 0)

  return (
    <Box className={qoClasses.card}>
      <Box className={classes.panelHead}>
        <Box miw={0}>
          <Text className={qoClasses.cardTitle}>{t("panel.title", { name: title })}</Text>
          <Text className={classes.sub}>{t("panel.sub")}</Text>
        </Box>
        <Text className={qoClasses.mono} c="var(--nw-text-2)">
          {date}
        </Text>
      </Box>

      {isLoading && <ListState state="loading" columns={6} rows={3} />}
      {!isLoading && !!error && <ListState state="error" title={t("match.errorTitle")} description={t("match.errorText")} onRetry={onRetry} />}
      {!isLoading && !error && lines.length === 0 && <ListState state="empty" title={t("panel.emptyTitle")} />}

      {!isLoading && !error && lines.length > 0 && (
        <>
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={52} />
                  <Table.Th>{t("panel.col.product")}</Table.Th>
                  <Table.Th w={100} ta="right">{t("panel.col.before")}</Table.Th>
                  <Table.Th w={100} ta="right">{t("panel.col.now")}</Table.Th>
                  <Table.Th w={128}>{t("panel.col.stock")}</Table.Th>
                  <Table.Th w={170}>{t("panel.col.status")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((line) => (
                  <PanelRow key={line.variantId} line={line} checked={isSelected(line)} onToggle={() => onToggle(line)} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Box className={qoClasses.cardFoot}>
            <Text className={qoClasses.hint}>
              {t("panel.selectedPrefix")}{" "}
              <Text span fw={700} c="var(--nw-text)">
                {selected.length}
              </Text>{" "}
              {t("panel.selectedSuffix")}{" "}
              <Text span fw={700} className={qoClasses.mono} c="var(--nw-text)">
                {currencyFormat(total)}
              </Text>
            </Text>
            <Button size="md" leftSection={<ShoppingCartSimpleIcon size={18} />} disabled={selected.length === 0} onClick={onAdd}>
              {t("match.add", { count: selected.length })}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

function StockCell({ line }: Readonly<{ line: CheckedLine }>) {
  const { t } = useTranslation("bulkOrder")
  if (line.status === "NOT_FOUND" || line.available == null) return <Text className={qoClasses.hint}>–</Text>
  const stock = line.available
  const [tone, label]: [Tone, string] =
    stock <= 0
      ? ["grey", t("panel.stockOut")]
      : stock < FEW_STOCK
        ? ["amber", t("panel.stockFew", { count: stock })]
        : ["green", t("panel.stockReady", { count: stock })]
  return (
    <Group gap={6} wrap="nowrap">
      <Dot tone={tone} />
      <Text fz="var(--nw-fz-body)">{label}</Text>
    </Group>
  )
}

function PanelRow({ line, checked, onToggle }: Readonly<{ line: CheckedLine; checked: boolean; onToggle: () => void }>) {
  const { t } = useTranslation("bulkOrder")
  const hint =
    line.status === "PRICE_CHANGED"
      ? t("panel.hintPriceChanged", { before: currencyFormat(line.previousPrice || 0), now: currencyFormat(line.currentPrice) })
      : line.status === "LOW_STOCK"
        ? t("status.lowStockHint", { count: line.addQty })
        : line.status === "OUT"
          ? t("panel.hintOut")
          : line.status === "NO_PRICE"
            ? t("panel.hintNoPrice")
            : line.status === "NOT_FOUND"
              ? t("panel.hintNotFound")
              : ""

  return (
    <Table.Tr>
      <Table.Td>
        <Checkbox checked={checked} disabled={!line.selectable} onChange={onToggle} aria-label={line.name || line.skuCode || ""} />
      </Table.Td>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Box className={qoClasses.thumb}>
            <ProductThumb src={line.image} alt={line.name || ""} iconSize={18} />
          </Box>
          <Box miw={0}>
            <Text fz="var(--nw-fz-body)" lineClamp={2}>
              {t("panel.lineName", { name: line.name || "-", count: line.quantity })}
            </Text>
            <Text className={qoClasses.mono} c="var(--nw-text-2)" translate="no">
              {line.skuCode || "-"}
            </Text>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td ta="right" className={qoClasses.mono}>
        {line.previousPrice ? currencyFormat(line.previousPrice) : "–"}
      </Table.Td>
      <Table.Td ta="right" className={qoClasses.mono}>
        {line.currentPrice > 0 ? currencyFormat(line.currentPrice) : "–"}
      </Table.Td>
      <Table.Td>
        <StockCell line={line} />
      </Table.Td>
      <Table.Td>
        <Stack gap={4} align="flex-start">
          <DotPill tone={STATUS_TONE[line.status]}>{t(`panel.status.${line.status}`)}</DotPill>
          {hint && <Text className={qoClasses.hint}>{hint}</Text>}
        </Stack>
      </Table.Td>
    </Table.Tr>
  )
}
